import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress, Slider } from "@mui/material";
import moment from "moment-timezone";
import { useSelector } from "react-redux";
import { getWorkSchedule, saveWorkSchedule } from "../api/workschedule";

const Toggle = ({ enabled, onChange }) => {
	return (
		<div
			style={{
				...styles.toggleContainer,
				backgroundColor: enabled ? "#3b82f6" : "#e5e7eb",
			}}
			onClick={onChange}>
			<div
				style={{
					...styles.toggleThumb,
					transform: enabled ? "translateX(20px)" : "translateX(2px)",
					backgroundColor: "white",
				}}
			/>
		</div>
	);
};

const TimeRangeSlider = ({ fromTime, toTime, onChange, enabled }) => {
	const timeToMinutes = time => {
		const [hours, minutes] = time.split(":").map(Number);
		return hours * 60 + minutes;
	};

	const minutesToTime = minutes => {
		const hours = Math.floor(minutes / 60);
		const mins = minutes % 60;
		return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
	};

	const formatDisplayTime = time => {
		const [hours, minutes] = time.split(":").map(Number);
		const period = hours >= 12 ? "pm" : "am";
		const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
		return `${displayHours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${period}`;
	};

	// Convert times to 30-minute intervals
	const roundToInterval = minutes => {
		return Math.round(minutes / 30) * 30;
	};

	const fromMinutes = roundToInterval(timeToMinutes(fromTime));
	const toMinutes = roundToInterval(timeToMinutes(toTime));
	const maxMinutes = 24 * 60; // 24 hours

	const handleSliderChange = (event, newValue) => {
		const [newFromMinutes, newToMinutes] = newValue;
		onChange(minutesToTime(newFromMinutes), minutesToTime(newToMinutes));
	};

	const valuetext = value => {
		return formatDisplayTime(minutesToTime(value));
	};

	return (
		<div style={styles.sliderContainer}>
			<Slider
				value={[fromMinutes, toMinutes]}
				onChange={handleSliderChange}
				valueLabelDisplay="off"
				getAriaValueText={valuetext}
				min={0}
				max={maxMinutes}
				step={30}
				disabled={!enabled}
				sx={{
					color: enabled ? "#3b82f6" : "#d1d5db",
					height: 4,
					"& .MuiSlider-thumb": {
						width: 16,
						height: 16,
						backgroundColor: enabled ? "#3b82f6" : "#9ca3af",
						border: "2px solid white",
						boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
						"&:hover": {
							boxShadow: enabled ? "0 0 0 8px rgba(59, 130, 246, 0.16)" : "none",
						},
					},
					"& .MuiSlider-track": {
						backgroundColor: enabled ? "#3b82f6" : "#d1d5db",
						border: "none",
						height: 4,
					},
					"& .MuiSlider-rail": {
						backgroundColor: "#e5e7eb",
						height: 4,
					},
				}}
			/>

			<div style={styles.timeLabels}>
				<span style={{ ...styles.timeLabel, color: enabled ? "#374151" : "#9ca3af" }}>{formatDisplayTime(minutesToTime(fromMinutes))}</span>
				<span style={{ ...styles.timeLabel, color: enabled ? "#374151" : "#9ca3af" }}>{formatDisplayTime(minutesToTime(toMinutes))}</span>
			</div>
		</div>
	);
};

const WorkingHoursPopup = () => {
	const token = useSelector(state => state.auth.token);

	const [open, setOpen] = useState(false);
	const [timezone, setTimezone] = useState(moment.tz.guess());
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [workingHours, setWorkingHours] = useState({
		Monday: { available: true, from: "09:00", to: "17:00" },
		Tuesday: { available: true, from: "08:00", to: "17:00" },
		Wednesday: { available: true, from: "08:00", to: "17:00" },
		Thursday: { available: true, from: "09:00", to: "17:00" },
		Friday: { available: true, from: "09:00", to: "17:00" },
		Saturday: { available: false, from: "09:00", to: "17:00" },
		Sunday: { available: false, from: "09:00", to: "17:00" },
	});

	const dayAbbreviations = {
		Monday: "M",
		Tuesday: "T",
		Wednesday: "W",
		Thursday: "T",
		Friday: "F",
		Saturday: "S",
		Sunday: "S",
	};

	const handleOpen = async () => {
		setOpen(true);
		setLoading(true);
		try {
			const schedule = await getWorkSchedule(token, timezone);
			setTimezone(schedule.timezone);

			const convertedSchedule = Object.keys(schedule.schedule).reduce((result, day) => {
				const { available, slots } = schedule.schedule[day];
				const firstSlot = slots[0] || { from: "09:00", to: "17:00" };
				result[day] = {
					available,
					from: firstSlot.from,
					to: firstSlot.to,
				};
				return result;
			}, {});
			setWorkingHours(convertedSchedule);
		} catch (error) {
			console.error("Error fetching work schedule:", error);
			// Keep default schedule
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => setOpen(false);

	const handleAvailabilityChange = day => {
		setWorkingHours({
			...workingHours,
			[day]: {
				...workingHours[day],
				available: !workingHours[day].available,
			},
		});
	};

	const handleTimeRangeChange = (day, from, to) => {
		setWorkingHours({
			...workingHours,
			[day]: {
				...workingHours[day],
				from,
				to,
			},
		});
	};

	const handleSave = async () => {
		setSaving(true);

		const convertedSchedule = Object.keys(workingHours).reduce((result, day) => {
			const { available, from, to } = workingHours[day];
			result[day] = {
				available,
				slots: available ? [{ from, to }] : [],
			};
			return result;
		}, {});

		const payload = {
			timezone,
			...convertedSchedule,
		};

		try {
			await saveWorkSchedule(token, payload);
			alert("Working hours saved successfully!");
			handleClose();
		} catch (error) {
			console.error("Error saving working hours:", error);
			alert("Failed to save working hours.");
		} finally {
			setSaving(false);
		}
	};

	return (
		<div style={styles.workingHoursContainer}>
			<button style={styles.setWorkingHoursButton} onClick={handleOpen}>
				Set working hours
			</button>

			<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
				<DialogTitle sx={styles.dialogTitle}>Working hours</DialogTitle>
				<DialogContent sx={styles.dialogContent}>
					{loading ? (
						<div style={styles.loadingContainer}>
							<CircularProgress sx={{ color: "#3b82f6" }} />
							<span style={styles.loadingText}>Loading...</span>
						</div>
					) : (
						<div style={styles.daysContainer}>
							{Object.keys(workingHours).map(day => (
								<div key={day} style={styles.dayRow}>
									<div style={styles.dayInfo}>
										<Toggle enabled={workingHours[day].available} onChange={() => handleAvailabilityChange(day)} />
										<span style={styles.dayAbbreviation}>{dayAbbreviations[day]}</span>
									</div>

									<TimeRangeSlider
										fromTime={workingHours[day].from}
										toTime={workingHours[day].to}
										onChange={(from, to) => handleTimeRangeChange(day, from, to)}
										enabled={workingHours[day].available}
									/>
								</div>
							))}
						</div>
					)}
				</DialogContent>
				<DialogActions sx={styles.dialogActions}>
					<Button onClick={handleSave} variant="contained" sx={styles.saveButton} disabled={saving}>
						{saving ? "Saving..." : "Save"}
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

const styles = {
	workingHoursContainer: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		height: "100%", // Take full height of parent
		width: "100%",
		position: "relative", // Added for better positioning
		transform: "translateY(-30px)", // Move button 10px upward
	},

	setWorkingHoursButton: {
		padding: "0.5rem 1rem",
		borderRadius: "11px",
		border: "none",
		background: "#ffffff",
		color: "#465C9E",
		fontSize: "0.875rem",
		cursor: "pointer",
		fontFamily: "inherit",
		fontWeight: "600",
		transition: "all 0.2s ease",
		":hover": {
			background: "#f9fafb",
		},
	},

	dialogTitle: {
		fontSize: "1.5rem",
		fontWeight: "600",
		color: "#3b82f6",
		textAlign: "center",
		paddingBottom: "8px",
	},

	dialogContent: {
		padding: "0 24px 16px 24px",
	},

	dialogActions: {
		justifyContent: "center",
		padding: "16px 24px 24px 24px",
	},

	saveButton: {
		backgroundColor: "#3b82f6",
		borderRadius: "25px",
		padding: "12px 32px",
		fontSize: "1rem",
		fontWeight: "500",
		textTransform: "none",
		minWidth: "120px",
		"&:hover": {
			backgroundColor: "#2563eb",
		},
	},

	loadingContainer: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		padding: "2rem",
		gap: "1rem",
	},

	loadingText: {
		color: "#6b7280",
		fontSize: "1rem",
	},

	daysContainer: {
		display: "flex",
		flexDirection: "column",
		gap: "1.5rem",
		marginTop: "8px",
	},

	dayRow: {
		display: "flex",
		alignItems: "center",
		gap: "1rem",
	},

	dayInfo: {
		display: "flex",
		alignItems: "center",
		gap: "0.75rem",
		minWidth: "60px",
	},

	dayAbbreviation: {
		color: "#374151",
		fontSize: "1rem",
		fontWeight: "500",
		width: "20px",
	},

	toggleContainer: {
		width: "40px",
		height: "22px",
		borderRadius: "11px",
		position: "relative",
		cursor: "pointer",
		transition: "background-color 0.2s ease",
	},

	toggleThumb: {
		width: "18px",
		height: "18px",
		borderRadius: "50%",
		position: "absolute",
		top: "2px",
		transition: "transform 0.2s ease",
		boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
	},

	sliderContainer: {
		flex: 1,
		display: "flex",
		flexDirection: "column",
		gap: "0.5rem",
		padding: "0 8px",
	},

	timeLabels: {
		display: "flex",
		justifyContent: "space-between",
		fontSize: "0.875rem",
		marginTop: "8px",
	},

	timeLabel: {
		fontWeight: "500",
	},
};

export default WorkingHoursPopup;
